// --- React ---
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Flex, Text, LabeledSelectBox, LabeledDateInput } from '@/components/ui';
import { Button } from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { DataTable } from '@/components/common/DataTable';

// --- API ---
import {
  useActivityLogs,
  useActivityLogUsersByCompany,
  useActivityLogUsersBySite,
  type ActivityLogsQueryParams,
  type ActivityLogRow,
} from '@/api/admin/useActivityLogs';
import { useGetAllCompanies, useGetSitesByCompanyId } from '@/api/admin/useCompanyManagement';

// --- 유틸 ---
import { describeActivity } from '@/utils/describeActivity';

// --- 타입 ---
import { type ColumnDef, type Row } from '@tanstack/react-table';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 서브 컴포넌트 ====================

/** 활동 로그 행 확장 시 표시되는 메타데이터(Input/Output) 상세 뷰 */
const MetadataDetail: React.FC<{ row: Row<ActivityLogRow> }> = ({ row }) => {
  const { theme } = useTheme();
  const metadata = row.original.metadata as Record<string, unknown> | null;

  if (!metadata) return <Text size="sm" color="textSecondary">데이터 없음</Text>;

  const input = metadata.input as Record<string, unknown> | undefined;
  const output = metadata.output as Record<string, unknown> | undefined;

  const codeStyle: React.CSSProperties = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 12,
    background: theme.colors.surface.layout.background,
    border: `1px solid ${theme.colors.border.container}`,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing[3],
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: 300,
    overflowY: 'auto',
  };

  return (
    <div style={{ padding: theme.spacing[4], display: 'flex', gap: theme.spacing[4] }}>
      {/* 요청 데이터 */}
      <div style={{ flex: 1 }}>
        <Text size="xs" style={{ fontWeight: theme.fontWeight.semibold, marginBottom: theme.spacing[2] }}>
          Input (요청)
        </Text>
        <pre style={codeStyle}>
          {input ? JSON.stringify(input, null, 2) : '—'}
        </pre>
      </div>
      {/* 응답 데이터 */}
      <div style={{ flex: 1 }}>
        <Text size="xs" style={{ fontWeight: theme.fontWeight.semibold, marginBottom: theme.spacing[2] }}>
          Output (응답)
        </Text>
        <pre style={codeStyle}>
          {output ? JSON.stringify(output, null, 2) : '—'}
        </pre>
      </div>
    </div>
  );
};

// ==================== 상수 ====================

const ALL = '';

// ==================== 컴포넌트 ====================

const UserLogPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { theme } = useTheme();

  // ==================== 상태 관리 ====================

  // 적용된 검색 파라미터 (API 호출용)
  const [applied, setApplied] = useState<ActivityLogsQueryParams>({
    page: 1,
    limit: 200,
  });

  // 필터 드래프트 (검색 버튼 클릭 전까지 임시 보관)
  const [draftCompanyId, setDraftCompanyId] = useState<string>(ALL);
  const [draftSiteId, setDraftSiteId] = useState<string>(ALL);
  const [draftUserId, setDraftUserId] = useState<string>(ALL);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');

  // ==================== API 훅 ====================

  const selectedCompanyId = draftCompanyId && draftCompanyId !== ALL ? Number(draftCompanyId) : undefined;
  const selectedSiteId = draftSiteId && draftSiteId !== ALL ? Number(draftSiteId) : undefined;
  const { data: companies } = useGetAllCompanies();
  const { data: sites } = useGetSitesByCompanyId(selectedCompanyId ?? 0, {
    enabled: !!selectedCompanyId,
  });
  const { data: companyUsers } = useActivityLogUsersByCompany(selectedCompanyId);
  const { data: siteUsers } = useActivityLogUsersBySite(selectedSiteId);

  // ==================== 파생 데이터 ====================

  /** 사용자 셀렉트 옵션 (사이트 > 회사 우선순위) */
  const userOptions = useMemo(() => {
    if (selectedSiteId) return siteUsers ?? [];
    if (selectedCompanyId) return companyUsers ?? [];
    return [];
  }, [selectedSiteId, selectedCompanyId, siteUsers, companyUsers]);

  // ==================== 이펙트 ====================

  /** 회사 변경 시 사이트·사용자 초기화 */
  useEffect(() => {
    setDraftSiteId(ALL);
    setDraftUserId(ALL);
  }, [draftCompanyId]);

  /** 사이트 변경 시 사용자 초기화 */
  useEffect(() => {
    setDraftUserId(ALL);
  }, [draftSiteId]);

  // ==================== 핸들러 ====================

  /**
   * 검색 실행.
   * 드래프트 필터 값을 API 파라미터로 변환하여 applied 상태에 반영.
   */
  const handleSearch = useCallback(() => {
    const userId = draftUserId && draftUserId !== ALL ? Number(draftUserId) : undefined;
    const companyId = draftCompanyId && draftCompanyId !== ALL ? Number(draftCompanyId) : undefined;
    const siteId = draftSiteId && draftSiteId !== ALL ? Number(draftSiteId) : undefined;

    let from: string | undefined;
    let to: string | undefined;
    if (draftFrom.trim()) {
      const d = new Date(draftFrom);
      if (!Number.isNaN(d.getTime())) from = d.toISOString();
    }
    if (draftTo.trim()) {
      const d = new Date(draftTo);
      if (!Number.isNaN(d.getTime())) to = d.toISOString();
    }

    setApplied({
      page: 1,
      limit: 200,
      userId,
      companyId,
      siteId,
      from,
      to,
    });
  }, [draftUserId, draftCompanyId, draftSiteId, draftFrom, draftTo]);

  // ==================== 테이블 데이터 ====================

  const { data, isLoading, error, refetch } = useActivityLogs(applied);
  const rows = data?.rows ?? [];

  // ==================== 테이블 컬럼 정의 ====================

  const columns = useMemo<ColumnDef<ActivityLogRow, any>[]>(() => [
    {
      accessorKey: 'createdAt',
      header: t(i18nKeys.common.user_log_col_time),
      cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString(),
      size: 160,
    },
    {
      accessorKey: 'userEmail',
      header: t(i18nKeys.common.user_log_col_user),
      cell: ({ row }) => {
        const r = row.original;
        if (r.userId == null) return '—';
        return `#${r.userId}${r.userEmail ? ` (${r.userEmail})` : ''}`;
      },
      size: 200,
    },
    {
      id: 'description',
      header: '설명',
      accessorFn: (row) => describeActivity(row),
      cell: ({ getValue }) => (
        <span style={{ fontWeight: theme.fontWeight.medium }}>{getValue<string>()}</span>
      ),
      size: 160,
    },
    {
      accessorKey: 'featureLabel',
      header: t(i18nKeys.common.user_log_col_feature),
      size: 100,
    },
    {
      accessorKey: 'actionLabel',
      header: t(i18nKeys.common.user_log_col_action),
      size: 80,
    },
    {
      accessorKey: 'isError',
      header: t(i18nKeys.common.user_log_col_error),
      cell: ({ getValue }) => getValue<boolean>() ? t(i18nKeys.common.user_log_yes) : t(i18nKeys.common.user_log_no),
      size: 60,
    },
    {
      accessorKey: 'message',
      header: t(i18nKeys.common.user_log_col_message),
      meta: {
        style: {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 11,
        },
      },
      size: 300,
    },
  ], [t, theme.fontWeight.medium]);

  // ==================== 렌더링 ====================

  return (
    <DefaultContents>
      <Flex direction="column" gap={4}>
        {/* 필터 바 — Company / Site / User / 날짜 범위 */}
        <div style={{ display: 'flex', gap: theme.spacing[3], alignItems: 'center', width: '100%' }}>
          {/* 회사 필터 */}
          <div style={{ flex: 1 }}>
            <LabeledSelectBox
              label="Company"
              value={draftCompanyId}
              options={[
                { label: t(i18nKeys.common.user_log_all), value: ALL },
                ...(companies ?? []).map(c => ({ label: c.name, value: String(c.id) })),
              ]}
              isEdit
              onChange={opt => setDraftCompanyId(String(opt.value))}
              width="100%"
              labelWidth="70px"
            />
          </div>
          {/* 사이트 필터 */}
          <div style={{ flex: 1 }}>
            <LabeledSelectBox
              label="Site"
              value={draftSiteId}
              options={[
                { label: t(i18nKeys.common.user_log_all), value: ALL },
                ...(sites ?? []).map(s => ({ label: s.name, value: String(s.id) })),
              ]}
              isEdit={!!selectedCompanyId}
              onChange={opt => setDraftSiteId(String(opt.value))}
              width="100%"
              labelWidth="50px"
            />
          </div>
          {/* 사용자 필터 */}
          <div style={{ flex: 1 }}>
            <LabeledSelectBox
              label="User"
              value={draftUserId}
              options={[
                { label: t(i18nKeys.common.user_log_all), value: ALL },
                ...userOptions.map(u => ({ label: `${u.name} (${u.email})`, value: String(u.id) })),
              ]}
              isEdit={!!selectedCompanyId}
              onChange={opt => setDraftUserId(String(opt.value))}
              width="100%"
              labelWidth="50px"
            />
          </div>
          {/* 시작 날짜 */}
          <div style={{ flex: 1 }}>
            <LabeledDateInput
              label={t(i18nKeys.common.user_log_from)}
              value={draftFrom}
              onChange={setDraftFrom}
              isEdit
              width="100%"
              labelWidth="50px"
            />
          </div>
          {/* 종료 날짜 */}
          <div style={{ flex: 1 }}>
            <LabeledDateInput
              label={t(i18nKeys.common.user_log_to)}
              value={draftTo}
              onChange={setDraftTo}
              isEdit
              width="100%"
              labelWidth="50px"
            />
          </div>
          {/* 검색 · 새로고침 버튼 */}
          <Flex gap={2} style={{ flexShrink: 0 }}>
            <Button variant="primary" type="button" width="auto" onClick={handleSearch}>
              {t(i18nKeys.common.user_log_search)}
            </Button>
            <Button
              variant="tertiaryNeutral"
              type="button"
              width="auto"
              onClick={() => void refetch()}
            >
              {t(i18nKeys.common.user_log_refresh)}
            </Button>
          </Flex>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <Text style={{ color: theme.colors.surface.feedback.error }}>
            {(error as Error).message}
          </Text>
        )}

        {/* 활동 로그 테이블 */}
        <DataTable
          data={rows}
          columns={columns}
          enablePagination
          pageSize={15}
          enableSorting
          enableFiltering
          enableExpanding
          getRowCanExpand={() => true}
          renderSubComponent={(row) => <MetadataDetail row={row} />}
          isLoading={isLoading}
          emptyMessage={t(i18nKeys.common.user_log_empty)}
          tableStyle={{ width: '100%', tableLayout: 'fixed' }}
        />
      </Flex>
    </DefaultContents>
  );
};

export default UserLogPage;
